import React, { useState } from 'react';

const questionsData = [
  {
    id: 'refs1',
    title: 'Are AI-simulated patients perceived as more realistic?',
    text: 'Easily measured with Likert-style surveys, this question assesses learner adoption and implementation.',
    references: [
      { text: "Austin W, Ashwood N, Capes N. Scoping Review: Medical Students' Perceptions of AI-Simulated Patients in Medical Education. OSF Preprints, 2023. ", href: 'https://osf.io/ekvqm/', label: 'doi:10.31219/osf.io/ekvqm' },
      { text: 'De Mattei, L, et al. Are Artificial Intelligence Virtual Simulated Patients (AI-VSP) a Valid Teaching Modality for Health Professional Students?. Clinical Simulation in Nursing, 2024. ', href: 'https://repositorio.unesp.br/entities/publication/2bd86a39-0440-4589-9175-b138040c14f1', label: 'doi:10.1016/j.ecns.2024.101594' },
      { text: 'Tanaka M, et al. Enhancing medical interview skills through AI training. JMIR Med Educ, 2024. ', href: 'https://doi.org/10.2196/58753', label: 'doi:10.2196/58753' },
    ]
  },
  {
    id: 'refs2',
    title: 'Do AI patients offer a lower-stress practice environment?',
    text: 'Assessed with self-efficacy and stress inventories to measure learner comfort.',
    references: [
      { text: 'Zhou H, et al. Simulated patient chatbot for training. JMIR Med Educ, 2024. ', href: 'https://doi.org/10.2196/53961', label: 'doi:10.2196/53961' },
      { text: 'Wang C, et al. MedSimAI: A generative AI agent for medical education. arXiv, 2024. ', href: 'https://arxiv.org/abs/2503.05793', label: 'arXiv:2503.05793' },
    ]
  },
  {
    id: 'refs3',
    title: 'Is knowledge retained longer with AI simulations?',
    text: 'Retention is measurable with delayed post-tests or spaced recall quizzes.',
    references: [
      { text: 'Zidoun Y, et al. AI-based simulators vs. simulated patients. BMC Med Educ, 2024. ', href: 'https://doi.org/10.1186/s12909-024-06236-x', label: 'doi:10.1186/s12909-024-06236-x' },
      { text: 'Arango-Ibanez JP, et al. Evidence-based learning with AI. JMIR Med Educ, 2024. ', href: 'https://doi.org/10.2196/54507', label: 'doi:10.2196/54507' },
      { text: 'Memrizz. Medical Students Are Loving This Virtual Patient Simulator. Memrizz Blog. ', href: 'https://www.memrizz.com/blogs/medical-students-are-loving-this-virtual-patient-simulator-heres-why', label: 'memrizz.com' },
    ]
  },
  {
    id: 'refs4',
    title: 'Do learners generate better clinical hypotheses?',
    text: 'Hypothesis generation can be scored using a blinded rubric or NLP evaluation.',
    references: [
        { text: 'Brügge, E., et al. Large language models improve clinical decision making of medical students through patient simulation and structured feedback: a randomized controlled trial. BMC Med Educ, 2024. ', href: 'https://bmcmededuc.biomedcentral.com/articles/10.1186/s12909-024-06399-7', label: 'doi:10.1186/s12909-024-06399-7' },
        { text: 'Hicke Y, et al. MedSimAI: Enhancing deliberate practice. arXiv, 2025. ', href: 'https://arxiv.org/abs/2503.05793', label: 'arXiv:2503.05793' },
        { text: 'Öncü S, et al. AI-powered standardized patients. BMC Med Educ, 2025. ', href: 'https://doi.org/10.1186/s12909-025-06877-6', label: 'doi:10.1186/s12909-025-06877-6' },
    ]
  },
  {
    id: 'refs5',
    title: 'Do learners sequence diagnostics more accurately?',
    text: 'Aligns with clinical reasoning goals; analyzed via transcripts or rubrics.',
    references: [
        { text: 'Hicke Y, et al. MedSimAI: Enhancing deliberate practice. arXiv, 2025. ', href: 'https://arxiv.org/abs/2503.05793', label: 'arXiv:2503.05793' },
        { text: 'Borg A, et al. Virtual patient simulations with social robotics. J Med Internet Res, 2025. ', href: 'https://doi.org/10.2196/63312', label: 'doi:10.2196/63312' },
    ]
  }
];

const KeyQuestionsSection: React.FC = () => {
  const [openRefs, setOpenRefs] = useState<Record<string, boolean>>({});

  const toggleReferences = (id: string) => {
    setOpenRefs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="key-questions" style={{ background: '#fff', padding: '4rem 1rem', textAlign: 'center' }}>
      <h2 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '1rem' }}>
        Validated by Research. Driven by Results.
      </h2>
      <p style={{ color: '#666', fontSize: '1.25rem', marginBottom: '3rem', maxWidth: '900px', margin: '0 auto 3rem auto' }}>
        SILO is built on a foundation of rigorous academic research. Explore the evidence demonstrating how our AI simulations improve learning outcomes, enhance clinical skills, and build learner confidence.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        {questionsData.map(q => (
          <div key={q.id} style={{
            background: '#f6fcfc',
            border: '1px solid #b2dfdb33',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 700, marginBottom: '1rem' }}>{q.title}</h3>
            <p style={{ color: '#555', fontSize: '1.1rem', flexGrow: 1 }}>{q.text}</p>
            <button
              onClick={() => toggleReferences(q.id)}
              style={{
                background: '#223A5E',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                marginTop: '1.5rem',
                textAlign: 'left',
                fontWeight: 'bold',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>View References</span>
              <span>{openRefs[q.id] ? '−' : '+'}</span>
            </button>
            {openRefs[q.id] && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f0f0', borderRadius: '8px' }}>
                {q.references.map((ref, index) => (
                  <div key={index} style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                    <span>{ref.text}</span>
                    <a href={ref.href} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none', marginLeft: '0.5rem' }}>
                      {ref.label}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default KeyQuestionsSection;